import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Card, TextField, Button, Typography, InputAdornment,
  IconButton, CircularProgress, Alert, Divider, useMediaQuery, useTheme
} from '@mui/material';
import {
  Visibility, VisibilityOff, Email as EmailIcon,
  Lock as LockIcon, MonitorHeart as HeartIcon,
  Vaccines as VaccineIcon, HealthAndSafety as HealthIcon,
  QueryStats as StatsIcon,
} from '@mui/icons-material';
import { authAPI } from '../services/api';

const BLUE = '#00A8E8';
const GREEN = '#86E000';
const DARK_BLUE = '#005B8F';

const features = [
  { icon: <HeartIcon sx={{ fontSize: 20 }} />, text: 'Real-time vitals monitoring' },
  { icon: <VaccineIcon sx={{ fontSize: 20 }} />, text: 'Multi-device BLE integration' },
  { icon: <StatsIcon sx={{ fontSize: 20 }} />, text: 'Analytics & health insights' },
  { icon: <HealthIcon sx={{ fontSize: 20 }} />, text: 'Secure patient data management' },
];

export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await authAPI.login(email, password);
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('isAuthenticated', 'true');
      onLogin(data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Check credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#f0f7fa' }}>

      {/* LEFT PANEL — Image + Brand */}
      {!isMobile && (
        <Box sx={{
          flex: 1, position: 'relative', overflow: 'hidden',
          background: `linear-gradient(160deg, ${DARK_BLUE} 0%, #003f66 100%)`,
        }}>
          {/* Background image */}
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=900&q=80)`,
            backgroundSize: 'cover', backgroundPosition: 'center',
            opacity: 0.18,
          }} />

          {/* Gradient overlay */}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(160deg, rgba(0,63,102,0.92) 0%, rgba(0,91,143,0.85) 50%, rgba(0,168,232,0.7) 100%)`,
          }} />

          {/* Heartbeat lines */}
          {[15, 50, 82].map((top, i) => (
            <Box key={i} sx={{ position: 'absolute', top: `${top}%`, left: 0, right: 0 }}>
              <svg viewBox="0 0 900 80" style={{ width: '110%', marginLeft: '-5%', opacity: 0.12 }}>
                <polyline fill="none" stroke={i === 1 ? GREEN : '#fff'} strokeWidth="1.5"
                  points="0,40 80,40 110,40 130,15 150,65 170,28 190,52 210,40 280,40 380,40 410,40 430,15 450,65 470,28 490,52 510,40 580,40 680,40 710,40 730,15 750,65 770,28 790,52 810,40 900,40" />
              </svg>
            </Box>
          ))}

          {/* Content */}
          <Box sx={{
            position: 'relative', zIndex: 1, height: '100%',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            px: 6, py: 8,
          }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '14px',
                background: `linear-gradient(135deg, ${BLUE}, ${GREEN})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 8px 24px rgba(0,168,232,0.5)`,
              }}>
                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 18, fontFamily: 'Montserrat, sans-serif' }}>EW</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.3rem', fontFamily: 'Montserrat, sans-serif', lineHeight: 1.1 }}>
                  Eros Wellness
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', letterSpacing: 2, textTransform: 'uppercase' }}>
                  AI Clinic Platform
                </Typography>
              </Box>
            </Box>

            <Typography variant="h3" sx={{
              color: '#fff', fontWeight: 800, lineHeight: 1.15, mb: 2,
              fontFamily: 'Montserrat, sans-serif', fontSize: { md: '2.2rem', lg: '2.6rem' }
            }}>
              Smart Health<br />
              <Box component="span" sx={{ color: GREEN }}>Monitoring</Box><br />
              Platform
            </Typography>

            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', mb: 5, maxWidth: 380, lineHeight: 1.7 }}>
              Manage patient health screenings, vitals, and reports — all in one secure platform.
            </Typography>

            {/* Feature list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {features.map((f, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    color: GREEN, width: 36, height: 36, borderRadius: '10px',
                    bgcolor: 'rgba(134,224,0,0.12)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {f.icon}
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem' }}>{f.text}</Typography>
                </Box>
              ))}
            </Box>

            {/* Bottom badge */}
            <Box sx={{ mt: 'auto', pt: 6 }}>
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1,
                bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 3,
                px: 2.5, py: 1, border: '1px solid rgba(255,255,255,0.12)',
              }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: GREEN, boxShadow: `0 0 8px ${GREEN}` }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.78rem' }}>
                  Secured with AES-256 encryption
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* RIGHT PANEL — Login Form */}
      <Box sx={{
        width: { xs: '100%', md: 480 }, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        p: { xs: 3, sm: 4 },
        bgcolor: '#fff',
        boxShadow: '-4px 0 40px rgba(0,0,0,0.06)',
      }}>
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Mobile logo */}
          {isMobile && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{
                width: 60, height: 60, borderRadius: '16px', mx: 'auto', mb: 2,
                background: `linear-gradient(135deg, ${BLUE}, ${GREEN})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20 }}>EW</Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: DARK_BLUE, fontFamily: 'Montserrat, sans-serif' }}>
                Eros Wellness
              </Typography>
            </Box>
          )}

          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5, fontFamily: 'Montserrat, sans-serif' }}>
            Welcome back
          </Typography>
          <Typography sx={{ color: '#64748b', mb: 4, fontSize: '0.9rem' }}>
            Sign in to the Admin Portal
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Email Address" type="email" value={email}
              onChange={e => setEmail(e.target.value)} required disabled={loading}
              sx={{ mb: 2.5 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: BLUE }} /></InputAdornment>
              }}
            />
            <TextField
              fullWidth label="Password" value={password}
              type={showPass ? 'text' : 'password'}
              onChange={e => setPassword(e.target.value)} required disabled={loading}
              sx={{ mb: 3.5 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: BLUE }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPass(!showPass)} size="small" edge="end">
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              type="submit" fullWidth variant="contained" size="large"
              disabled={loading}
              sx={{
                py: 1.6, fontSize: '1rem', borderRadius: 3, fontWeight: 700,
                background: `linear-gradient(135deg, ${BLUE} 0%, ${GREEN} 100%)`,
                boxShadow: `0 6px 20px rgba(0,168,232,0.35)`,
                '&:hover': { boxShadow: `0 8px 28px rgba(0,168,232,0.45)`, transform: 'translateY(-1px)' },
                transition: 'all 0.2s ease',
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In to Portal'}
            </Button>
          </form>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', px: 1 }}>Patient?</Typography>
          </Divider>

          <Button
            component={Link} to="/patient-login"
            fullWidth variant="outlined" size="large"
            sx={{
              py: 1.5, borderRadius: 3, fontWeight: 600, fontSize: '0.9rem',
              borderColor: GREEN, color: DARK_BLUE,
              '&:hover': { borderColor: GREEN, bgcolor: 'rgba(134,224,0,0.06)' }
            }}
          >
            View My Health Report
          </Button>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
              Eros Wellness Platform © {new Date().getFullYear()} Eros Universe
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Typography
                component={Link} to="/privacy-policy"
                variant="caption"
                sx={{ color: BLUE, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Privacy Policy
              </Typography>
              <Typography variant="caption" sx={{ color: '#cbd5e1' }}>·</Typography>
              <Typography
                component={Link} to="/terms"
                variant="caption"
                sx={{ color: BLUE, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Terms &amp; Conditions
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
